import React, { useEffect, useState } from 'react';
import {
  Button, Form, Input, Modal, Popconfirm, Select, Table, Tag, message,
} from 'antd';
import {
  createLearnedAssociation,
  listLearnedAssociations,
  removeLearnedAssociation,
  updateLearnedAssociation,
} from '../../services/customerImportApi';

const MATCH_TYPE_OPTIONS = [
  { value: '', label: 'All match types' },
  { value: 'customer', label: 'Customer' },
  { value: 'shipto', label: 'Ship-To' },
  { value: 'product', label: 'Product' },
];

// Same three Match Types as the filter above, minus the "All" option --
// a record itself must have a specific, single Match Type.
const RECORD_MATCH_TYPE_OPTIONS = MATCH_TYPE_OPTIONS.filter((o) => o.value);

export default function LearnedAssociationsPanel() {
  const [records, setRecords] = useState([]);
  const [matchType, setMatchType] = useState('');
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = async (type) => {
    setLoading(true);
    try {
      const data = await listLearnedAssociations(type || undefined);
      setRecords(data || []);
    } catch (err) {
      message.error(err.message || 'Could not load learned associations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(matchType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchType]);

  const handleRemove = async (recordId) => {
    setRemovingId(recordId);
    try {
      const result = await removeLearnedAssociation(recordId);
      if (result && result.removed === false) {
        message.warning('Record was already removed or not found.');
      } else {
        message.success('Learning record removed.');
      }
      await load(matchType);
    } catch (err) {
      message.error(err.message || 'Could not remove learning record.');
    } finally {
      setRemovingId(null);
    }
  };

  const openAddEditor = () => {
    setEditingRecord(null);
    form.resetFields();
    setEditorOpen(true);
  };

  const openEditEditor = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      match_type: record.match_type,
      old_value: record.old_value,
      new_value: record.new_value,
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return; // antd already renders the field-level errors
    }
    setSaving(true);
    try {
      if (editingRecord) {
        await updateLearnedAssociation(editingRecord.id, values);
        message.success('Learning record updated.');
      } else {
        await createLearnedAssociation(values);
        message.success('Learning record added.');
      }
      closeEditor();
      await load(matchType);
    } catch (err) {
      message.error(err.message || 'Could not save learning record.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Match Type', dataIndex: 'match_type' },
    { title: 'Source Entity', dataIndex: 'source_entity_external_id' },
    {
      title: 'Override',
      key: 'override',
      render: (_, record) => (
        <div>
          <div className="text-muted small">{record.field_name}</div>
          <div>{record.old_value} → {record.new_value}</div>
        </div>
      ),
    },
    {
      title: 'When',
      dataIndex: 'created_at',
      render: (value) => (value ? new Date(value).toLocaleString() : '—'),
    },
    { title: 'By Whom', dataIndex: 'actor_id' },
    {
      title: 'Origin',
      dataIndex: 'entry_source',
      render: (value) => (value === 'manual' ? <Tag>Manually added</Tag> : null),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        record.disabled_at
          ? <Tag color="default">Disabled</Tag>
          : <Tag color="green">Active</Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Button size="small" onClick={() => openEditEditor(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Remove this learning record?"
            description="This soft-disables it immediately -- there is no undo in this view."
            okText="Yes"
            cancelText="No"
            disabled={Boolean(record.disabled_at)}
            onConfirm={() => handleRemove(record.id)}
          >
            <Button
              size="small"
              danger
              disabled={Boolean(record.disabled_at)}
              loading={removingId === record.id}
            >
              Remove
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-3 d-flex justify-content-between align-items-end">
        <div style={{ maxWidth: 260 }}>
          <Select
            value={matchType}
            onChange={setMatchType}
            options={MATCH_TYPE_OPTIONS}
            style={{ width: '100%' }}
          />
        </div>
        <Button type="primary" onClick={openAddEditor}>
          Add Record
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={records}
        columns={columns}
        pagination={false}
        locale={{ emptyText: 'No learned associations.' }}
      />
      <Modal
        title={editingRecord ? 'Edit learning record' : 'Add learning record'}
        open={editorOpen}
        onCancel={closeEditor}
        onOk={handleSave}
        confirmLoading={saving}
        okText={editingRecord ? 'Save' : 'Add'}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ match_type: 'product' }}>
          <Form.Item
            name="match_type"
            label="Match Type"
            rules={[{ required: true, message: 'Match Type is required.' }]}
          >
            <Select options={RECORD_MATCH_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="old_value"
            label="Old Value (the system's current recommendation to correct away from)"
            rules={[{ required: true, message: 'Old Value is required.' }]}
          >
            <Input placeholder="e.g. SKU-1" />
          </Form.Item>
          <Form.Item
            name="new_value"
            label="New Value (the correct match to apply instead)"
            rules={[{ required: true, message: 'New Value is required.' }]}
          >
            <Input placeholder="e.g. SKU-2" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
